import React, { useEffect, useState } from "react";
import { LogIn } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@pgs-core/ui/components/alert-dialog";
import { tokenExpiryMs } from "../lib/claims";
import type { ApiClient } from "../lib/api";

interface ActAsExpiryModalProps {
  /** The api client holding the act-as token whose expiry is watched. */
  api: ApiClient;
  /** The admin portal origin to send the admin back to (from the handover). */
  returnTo: string;
  /**
   * Increments every time an API call stays 401 — the server rejected the
   * token. Forces the dialog open regardless of the local clock.
   */
  expiredSignal: number;
}

const TICK_MS = 30_000;

/**
 * End-of-session dialog for an act-as session. Act-as tokens are deliberately
 * not refreshable (see packages/auth/src/act-as.ts — the TTL outlasts a
 * working day instead), so when one expires the only path forward is opening
 * a fresh session from the Admin Portal.
 */
export function ActAsExpiryModal({ api, returnTo, expiredSignal }: ActAsExpiryModalProps) {
  const [expiredLocally, setExpiredLocally] = useState(false);

  useEffect(() => {
    const tick = () => {
      const expiry = tokenExpiryMs(api.getToken());
      setExpiredLocally(expiry !== null && expiry <= Date.now());
    };
    tick();
    const interval = setInterval(tick, TICK_MS);
    // Background tabs throttle (or suspend) timers — recompute the moment the
    // tab is visible/focused again instead of waiting for the next interval.
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [api]);

  const open = expiredLocally || expiredSignal > 0;

  return (
    // Controlled open with no onOpenChange: Escape / overlay clicks are inert —
    // an expired session can't keep being used, only reopened.
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Act-as session expired</AlertDialogTitle>
          <AlertDialogDescription>
            This act-as session has ended, so further changes can't be saved.
            Reopen the client's portal from the Admin Portal to keep working.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {returnTo ? (
            <Button onClick={() => window.location.assign(returnTo)}>
              <LogIn className="h-4 w-4" />
              Back to Admin Portal
            </Button>
          ) : (
            <Button onClick={() => window.close()}>Close this tab</Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
